import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges } from '@angular/core';
import { IGenomeTime } from 'bz-genome-angular/time-picker/time-picker/bzg-time-picker.component';
import { GenomeTimeService } from 'app/shared/services/genome-time.service';
import { EJobType } from '../job-type.enum';
import { BzgModalService, BzgToastService, BzgToastType } from 'bz-genome-angular';
import { DeleteConfirmationComponent } from 'app/shared/components/delete-confirmation/delete-confirmation.component';
import { UserJobsService } from 'app/scheduler/user-Jobs/user-jobs.service';
import { TranslateService } from '@ngx-translate/core';
import { I18nService } from 'i18n/i18n.service';
import { IJob } from '../IJob';
import { DateUtil } from 'app/core/utils/DateUtil';

@Component({
  selector: 'job-settings',
  templateUrl: 'job-settings.component.html',
  styleUrls: ['job-settings.component.scss']
})
export class JobSettingsComponent implements OnChanges {

  @Input() applyDisabled = false;
  @Input() currentJob: IJob;
  @Input() jobType: EJobType;
  @Output() saveJob = new EventEmitter<IJob>();
  @Output() cancel = new EventEmitter();
  @Output() reset = new EventEmitter();

  public showReset: boolean = false;
  public nextRuntimeDate: Date;
  public nextRuntimeDateTime: any;
  public jobStates: { [key: string]: { rescheduleJob: boolean, switchActivated: boolean, originalNextRuntimeDate: Date, nextRuntimeDate: Date } } = {};

  @ViewChild('dpTo', { static: false }) dpTo: any;

  constructor(private _modalService: BzgModalService,
    private _userJobService: UserJobsService,
    private _toastService: BzgToastService,
    private _i18nService: I18nService) {
    this.nextRuntimeDateTime = {
      hours: 12,
      minutes: 0,
      seconds: 0,
      period: 'AM'
    };
  }

  ngOnChanges() {
    if (!this.jobStates[this.currentJob.guid]) {
      this.jobStates[this.currentJob.guid] = {
        rescheduleJob: false,
        switchActivated: false,
        originalNextRuntimeDate: new Date(this.currentJob.nextRuntime),
        nextRuntimeDate: DateUtil.convertFromLocalToUTCReturnDate(this.currentJob.nextRuntime)
      };
    }
    this.nextRuntimeDate = this.jobStates[this.currentJob.guid].nextRuntimeDate;
    this.nextRuntimeDateTime = DateUtil.getTimeFromDate(this.nextRuntimeDate);
    if (this.currentJob.disableEditJob && this.dpTo && this.dpTo.onShown) {
      this.dpTo.onShown.isStopped = true;
    }
    if (this.currentJob)
      this.getStatusReset();
  }

  public setJobNow() {
    this.nextRuntimeDate = DateUtil.convertFromLocalToUTCReturnDate(new Date());
    this.jobStates[this.currentJob.guid].nextRuntimeDate = this.nextRuntimeDate;
    this.nextRuntimeDateTime = DateUtil.getTimeFromDate(this.nextRuntimeDate);
  }

  public setJobTo9999() {
    this.nextRuntimeDate = new Date(Date.UTC(9999, 11, 31, 23, 59, 59));
    this.jobStates[this.currentJob.guid].nextRuntimeDate = this.nextRuntimeDate;
    this.nextRuntimeDateTime = DateUtil.getTimeFromDate(this.nextRuntimeDate);
  }

  public applyChanges() {
    DateUtil.setTimeToDate(this.nextRuntimeDate, this.nextRuntimeDateTime);
    this.currentJob.nextRuntime = DateUtil.returnDateToSave(this.nextRuntimeDate);
    this.jobStates[this.currentJob.guid].nextRuntimeDate = this.nextRuntimeDate;
    if (this.currentJob.schedules) {
      this.currentJob.schedules.forEach(schedule => {
        schedule.nextRunTime = this.currentJob.nextRuntime;
      });
    }
    this.saveJob.emit(this.currentJob);
    this.getStatusReset();
  }

  public cancelChanges() {
    DateUtil.setTimeToDate(this.nextRuntimeDate, this.nextRuntimeDateTime);
    this.currentJob.nextRuntime = this.nextRuntimeDate;
    this.jobStates[this.currentJob.guid].nextRuntimeDate = this.nextRuntimeDate;
    this.cancel.emit();
  }

  public isUserJob() {
    return this.jobType === EJobType.User;
  }

  public resetJob() {
    const title = `${this._i18nService.instant('GLOBAL.RESET')} - ${this.currentJob.name}`
    const modal = this._modalService.open(DeleteConfirmationComponent, {
      closeOnBackdropClick: false,
      closeOnRouterNavigation: true,
      data: { title, message: 'GLOBAL.CONFIRM_RESET', button: 'GLOBAL.RESET' }
    });
    modal.afterClosed().subscribe((result) => {
      if (result === 'delete') {
        this._userJobService.resetStatus(this.currentJob.guid).subscribe((response) => {
          if (response)
            this.showToast(this._i18nService.instant('GLOBAL.THE_OPERATION_WAS_SAVED_SUCCESSFULLY'), 'success', 5000);
        });
      }
    });
  }

  public getStatusReset() {
    if (this.currentJob.guid) {
      this._userJobService.getStatusReset(this.currentJob.guid).subscribe((response) => {
        this.showReset = response;
      });
    }
  }

  showToast(messageInput: string, type: BzgToastType, duration: number) {
    this.reset.emit();
    this.getStatusReset();
    this._toastService.open(
      messageInput,
      {
        type,
        duration: duration,
        verticalPosition: 'bottom',
        horizontalPosition: 'right'
      });
  }

  public checkAndSetDate() {
    const jobState = this.jobStates[this.currentJob.guid];
    if (jobState.rescheduleJob) {
      this.setJobNow();
      jobState.switchActivated = true;
    } else {
      this.setJobTo9999();
      jobState.switchActivated = false;
    }
    jobState.nextRuntimeDate = this.nextRuntimeDate;
  }

  public isNextRuntimeYear9999(): boolean {
    return this.jobStates[this.currentJob.guid].originalNextRuntimeDate.getFullYear() === 9999;
  }

  public shouldShowDateSection(): boolean {
    const jobState = this.jobStates[this.currentJob.guid];
    return !this.isNextRuntimeYear9999() || jobState.rescheduleJob;
  }

  public shouldShowSwitch(): boolean {
    const jobState = this.jobStates[this.currentJob.guid];
    return this.isNextRuntimeYear9999() || jobState.switchActivated;
  }

  public shouldDisableButton(): boolean {
    const jobState = this.jobStates[this.currentJob.guid];
    return this.shouldShowSwitch() && !jobState.rescheduleJob;
  }
}